import { t } from '../../../../../locales/index';
import { api } from '../../../../plugins/axios';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useAuthStore } from '../../../../store/shared/auth-store';
import { db } from '@/firebase';
import { messageTypeEnum } from './enums';
import { getMessageConfig } from './configs';
import { assignSelectedConversationToMe } from '@/v2/modules/whatsapp/entities/chats/helper';

const sendMessageEP = process.env.MIX_API_FUNCTION_URL_SEND_MESSAGE_TO_WHATSAPP;

export default class Message {
	message_id = undefined;
	is_new = undefined;
	sent_by_merchant = undefined;
	timestamp = undefined;
	type = undefined;
	profile_name = undefined;
	extra_data = undefined;
	message_status = undefined;
	from = undefined;
	message_errors = undefined;
	message_metadata = undefined;
	reply_message = undefined;
	content = undefined;
	repliedMessageData = undefined;
	linker_order_id = undefined;
	audioPlaying = undefined;
	transcript = undefined;

	constructor(obj) {
		Object.keys(obj).forEach((key) => {
			this[key] = obj[key];
		});
		if (this.type === messageTypeEnum.Audio) {
			this.audioPlaying = false;
		}
	}

	get canForward() {
		const config = getMessageConfig(this);
		return config.canForward;
	}

	get canDownload() {
		const config = getMessageConfig(this);
		return config.canDownload;
	}

	get formattedMessageDate() {
		let date;

		// Handle missing timestamp
		if (!this.timestamp) {
			return '';
		}

		// Handle ISO string timestamps (e.g., "2025-05-30T10:00:00.000Z")
		if (typeof this.timestamp === 'string' && this.timestamp.includes('T')) {
			date = new Date(this.timestamp);
		} else {
			// Handle numeric timestamps (could be seconds or milliseconds)
			const ts = Number(this.timestamp);
			const timestampLength = ts.toString().length;

			// If 10-11 digits = seconds, multiply by 1000 to get milliseconds
			// If 13+ digits = milliseconds or more, divide appropriately
			let milliseconds;
			if (timestampLength <= 11) {
				// Timestamp is in seconds, convert to milliseconds
				milliseconds = ts * 1000;
			} else if (timestampLength <= 13) {
				// Timestamp is already in milliseconds
				milliseconds = ts;
			} else {
				// Timestamp is in microseconds or nanoseconds
				milliseconds = Math.floor(ts / Math.pow(10, timestampLength - 13));
			}

			date = new Date(milliseconds);
		}

		// Check for invalid date
		if (isNaN(date.getTime())) {
			return '';
		}
		const currentDate = new Date();

		if (
			date.getDate() === currentDate.getDate() &&
			date.getMonth() === currentDate.getMonth() &&
			date.getFullYear() === currentDate.getFullYear()
		) {
			return t('Today').toUpperCase();
		}

		currentDate.setDate(currentDate.getDate() - 1);

		if (
			date.getDate() === currentDate.getDate() &&
			date.getMonth() === currentDate.getMonth() &&
			date.getFullYear() === currentDate.getFullYear()
		) {
			return t('Yesterday').toUpperCase();
		}

		const day = date.getDate().toString().padStart(2, '0');
		const month = (date.getMonth() + 1).toString().padStart(2, '0');
		const year = date.getFullYear();

		return `${day}/${month}/${year}`;
	}

	get hasReplyMessage() {
		if (!this.reply_message) return false;
		if (this.reply_message.mid || this.reply_message.id) return true;
		if (this.reply_message.referred_product) return true;
		return false;
	}

	static sendMessage(data, channelType = 'whatsapp') {
		assignSelectedConversationToMe();
		const authStore = useAuthStore();

		return api
			.post(
				sendMessageEP,
				{
					...data,
					extra_data: {
						user_id: authStore.user.id, // Returned from users API
						full_name: authStore.user.full_name, // Returned from users API
						...data.extra_data,
					},
				},
				{ params: { channelType } },
			)
			.then((res) => res.data);
	}

	async fetchRepliedMessageData({ conversationId, channelType }) {
		if (this.repliedMessageData) return;
		const authStore = useAuthStore();
		const tenantId = authStore.tenantId;

		const referredProductId =
			this.reply_message?.referred_product?.product_retailer_id ?? undefined;
		if (referredProductId) {
			const message = new Message({
				type: messageTypeEnum.ProductReplied,
				sent_by_merchant: true,
				customData: {
					referredProduct: this.reply_message?.referred_product,
					referredProductId,
				},
			});
			this.repliedMessageData = message;
			return message;
		}
		const messagesRef = collection(
			db,
			`tenants/${tenantId}/channels/${channelType}/conversations/${conversationId}/messages`,
		);
		const repliedMessageId = ['whatsapp', 'whatsapp-qr'].includes(channelType)
			? this.reply_message.id
			: this.reply_message.mid;
		const q = query(messagesRef, where('message_id', '==', `${repliedMessageId}`));
		const querySnapshot = await getDocs(q);
		const messages = querySnapshot.docs.map((doc) => ({
			id: doc.id,
			...doc.data(),
		}));
		const data = messages.length ? messages[0] : {};

		const message = new Message(data);

		this.repliedMessageData = message;

		return message;
	}

	sendOrderSummary() {
		if (!this.linker_order_id) {
			console.error(
				'[WhatsApp V2]: You cannot call setOrderSummary, because linker_order_id is undefined',
			);
			return;
		}
		return api.post('/main-order/payment/order-summary', {
			order_id: this.linker_order_id,
		});
	}

	async fetchTranscript(conversationId, channelType = 'messenger') {
        if (this.transcript !== undefined) return;
        this.transcript = null;

        const mediaLink = this.content[this.type]?.link;

        if (!mediaLink) return;

        const authStore = useAuthStore();
        const tenantId = authStore.tenantId;
        if(channelType === 'messenger') {
            conversationId = channelType.concat('-', conversationId);
        }
        console.log(`tenants/${tenantId}/system-messages/${conversationId}/messages`);
        const messagesRef = collection(
            db,
            `tenants/${tenantId}/system-messages/${conversationId}/messages`,
        );
        const q = query(messagesRef, where('payload.url', '==', mediaLink));
        const querySnapshot = await getDocs(q);
        const messages = querySnapshot.docs.map((doc) => doc.data());
        const message = messages.length ? messages[0] : null;
        if (message?.payload?.transcript) {
            this.transcript = message.payload.transcript;
        }
    }

	static sendTemplate(data) {
		return api.post('/v1/whatsapp/marketing/whatsapp-templates/send', data);
	}
}
